import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Button, ErrorNote, Modal } from "@/components/ui";
import { LineFields } from "@/components/LineFields";
import {
  lineDraftIsValid,
  lineDraftToPatch,
  lineToDraft,
  type LineDraft,
} from "@/lib/bookingLines";
import { tServiceKind } from "@/i18n";
import type { BookingLineDetail } from "@/lib/types";

/** Phase 13.2 "Edit" on a booking-line card: every commercial and dispatch
 * field of the line in one dialog, saved as a PATCH of just what changed.
 *
 * The backend does the follow-through — a new pickup time moves the dispatch
 * task (and so the calendar event), prices re-derive the margin, and a driver
 * who already got the card is sent an "Updated booking" one. */
export function EditBookingLineModal({
  bookingRef,
  line,
  currency,
  customerName,
  invalidate,
  onClose,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  currency: string;
  customerName: string;
  invalidate: unknown[][];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The baseline is the line as saved; the working copy also pre-fills a blank
  // tourist name from the order's customer, which then counts as an edit.
  const original = useMemo(() => lineToDraft(line), [line]);
  const [draft, setDraft] = useState<LineDraft>(() => lineToDraft(line, customerName));
  const save = useEntityAction(invalidate, { inlineError: true, onSuccess: onClose });

  const patch = lineDraftToPatch(original, draft, currency);
  const changed = Object.keys(patch).length > 0;
  const notifiesDriver = line.driver_guide_id != null;

  return (
    <Modal
      title={`${t("bookingLines.editTitle")} — ${tServiceKind(line.service_kind)}`}
      onClose={onClose}
      busy={save.isPending}
    >
      <LineFields
        value={draft}
        onChange={(p) => setDraft((prev) => ({ ...prev, ...p }))}
        showDriver={false}
        lockKind
      />
      {notifiesDriver && (
        <p className="mt-3 text-xs text-sand-muted">{t("bookingLines.editNotifiesDriver")}</p>
      )}
      {save.isError && (
        <div className="mt-3">
          <ErrorNote message={(save.error as Error).message} />
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button
          loading={save.isPending}
          disabled={!changed || !lineDraftIsValid(draft)}
          onClick={() =>
            save.mutate(() => api.patch(`/bookings/${bookingRef}/lines/${line.id}`, patch))
          }
        >
          {t("common.save")}
        </Button>
        <button
          className="text-sm text-sand-muted hover:text-sand disabled:opacity-50"
          disabled={save.isPending}
          onClick={onClose}
        >
          {t("common.cancel")}
        </button>
      </div>
    </Modal>
  );
}
